/**
 * BidCell Component (v2.3 - CSS Media Queries)
 *
 * Displays a bid in the auction table with optional popup for comments.
 * - Bids with comments have light yellow background
 * - Hover shows popup on desktop (200ms delay)
 * - Tap toggles popup on touch devices
 * - Only one popup open at a time (controlled by parent)
 * - Uses CSS media queries for responsive sizing (no JS viewport detection)
 */

import { useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface BidCellProps {
  bidText: string;
  comment?: string | null;
  isPopupOpen: boolean;
  onOpenPopup: () => void;
  onClosePopup: () => void;
  /**
   * @deprecated Retained for backwards-compatibility with AuctionTable's call
   * site. Tooltip placement is now computed dynamically from the trigger's
   * viewport rect (see the portal positioning effect below), so this hint is
   * no longer consulted.
   */
  columnPosition?: 'left' | 'center' | 'right';
  /**
   * Optional renderer consulted BEFORE the default one (feature 029). Return `null` for any call
   * the caller does not want to handle and the default renderer takes it — so a caller can add a
   * call shape without owning, or drifting from, the shared suit markup.
   *
   * Omit it and this component behaves exactly as it did before the prop existed; that is the
   * whole contract, and the unedited `BidCell` suite is what demonstrates it.
   */
  callFormatter?: (callText: string) => JSX.Element | null;
}

/** Computed fixed-position placement for the portalled tooltip. */
interface TooltipPlacement {
  top: number;
  left: number;
  /** 'top' = tooltip sits above the trigger; 'bottom' = below. */
  placement: 'top' | 'bottom';
  /** Arrow offset (px) from the tooltip's left edge, pointing at the trigger. */
  arrowLeft: number;
  /** False until the tooltip has been measured, so we can render it invisibly first. */
  ready: boolean;
}

/**
 * The default bid renderer: suit bids as level + coloured suit symbol, the special calls
 * (Pass / X / XX / ? / -) in their canonical form, and anything it does not recognise as its own
 * literal text.
 *
 * Hoisted out of `BidCell` and exported (feature 029) so a caller supplying its own
 * `callFormatter` can handle the one or two call shapes it cares about and DELEGATE everything
 * else back here, instead of reimplementing the suit markup and drifting from it. Pure — it reads
 * nothing but its argument, which is why hoisting it changes no behaviour.
 *
 * FR-019: 1.5em suit symbols in the auction; FR-020: 0.15em gap between level and suit/NT.
 * Responsive sizing comes from the CSS `short:` variant.
 */
  // Format the bid display with suit symbols
  // FR-019: 1.5em suit symbols in auction; FR-020: 0.15em gap between level and suit/NT
  // Uses CSS classes for responsive sizing via short: variant
export function formatBidDefault(callText: string): JSX.Element | null {
  if (!callText) return null;

  // Handle special bids
  if (callText === '-') return <span className="text-black">-</span>;
  if (callText === '?') return <span className="text-black">?</span>;
  if (callText.toLowerCase() === 'pass') {
    return <span className="text-black">Pass</span>;
  }
  if (callText === 'P' || callText === 'p') {
    return <span className="text-black">P</span>;
  }
  if (callText === 'X' || callText.toLowerCase() === 'double' || callText.toLowerCase() === 'dbl') {
    return <span className="text-black">X</span>;
  }
  if (callText === 'XX' || callText.toLowerCase() === 'redouble' || callText.toLowerCase() === 'rdbl') {
    return <span className="text-black">XX</span>;
  }

  // Format suit bids with symbols
  const match = callText.match(/^(\d)(C|D|H|S|NT)$/i);
  if (match) {
    const level = match[1];
    const suitChar = match[2]?.toUpperCase();

    // FR-019: Suit symbols at 1.5em in auction table
    // FR-020: 0.15em gap between level number and suit/NT text
    const suitDisplay: Record<string, JSX.Element> = {
      C: (
        <span>
          <span className="text-black font-semibold align-middle">{level}</span>
          <span className="text-black text-[1.5rem] align-middle font-suits" style={{ marginLeft: '0.15em' }}>{'\u2663'}</span>
        </span>
      ),
      D: (
        <span>
          <span className="text-black font-semibold align-middle">{level}</span>
          <span className="text-red-600 text-[1.5rem] align-middle font-suits" style={{ marginLeft: '0.15em' }}>{'\u2666'}</span>
        </span>
      ),
      H: (
        <span>
          <span className="text-black font-semibold align-middle">{level}</span>
          <span className="text-red-600 text-[1.5rem] align-middle font-suits" style={{ marginLeft: '0.15em' }}>{'\u2665'}</span>
        </span>
      ),
      S: (
        <span>
          <span className="text-black font-semibold align-middle">{level}</span>
          <span className="text-black text-[1.5rem] align-middle font-suits" style={{ marginLeft: '0.15em' }}>{'\u2660'}</span>
        </span>
      ),
      NT: (
        <span>
          <span className="text-black font-semibold align-middle">{level}</span>
          <span className="text-black align-middle" style={{ marginLeft: '0.15em' }}>NT</span>
        </span>
      ),
    };

    return suitDisplay[suitChar || ''] || <span>{callText}</span>;
  }

return <span>{callText}</span>;
}

export function BidCell({
  bidText,
  comment = null,
  isPopupOpen,
  onOpenPopup,
  onClosePopup,
  callFormatter,
}: BidCellProps) {
  const bid = bidText;

  // The caller's renderer first, the shared default for everything it declines (feature 029).
  // With no callFormatter this is `formatBidDefault` and nothing else.
  const formatBid = (callText: string) => callFormatter?.(callText) ?? formatBidDefault(callText);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<TooltipPlacement>({
    top: 0, left: 0, placement: 'top', arrowLeft: 16, ready: false,
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Dynamic tooltip positioning (fixes top/edge clipping reported on phones).
  // The auction card is `overflow-hidden`, so an in-flow tooltip opening upward
  // from a top auction row gets its top edge clipped. We render the tooltip in a
  // portal to document.body and position it with `position: fixed` computed from
  // the trigger's viewport rect — escaping every overflow-hidden ancestor — then:
  //   • flip above ↔ below depending on which side has room (top rows open down),
  //   • clamp the left edge so a lengthy alert never spills past the viewport.
  // Re-measures on scroll/resize while open so it tracks the trigger.
  useLayoutEffect(() => {
    if (!isPopupOpen || !comment) {
      setTip((prev) => (prev.ready ? { ...prev, ready: false } : prev));
      return;
    }

    const compute = () => {
      const trigger = triggerRef.current;
      const tooltip = tooltipRef.current;
      if (!trigger || !tooltip) return;

      const r = trigger.getBoundingClientRect();
      const tw = tooltip.offsetWidth;
      const th = tooltip.offsetHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const MARGIN = 8; // keep this far from the viewport edge
      const GAP = 6;    // gap between trigger and tooltip

      // Vertical: prefer above; flip below when there isn't room above (top rows).
      const spaceAbove = r.top;
      const spaceBelow = vh - r.bottom;
      const placement: 'top' | 'bottom' =
        spaceAbove >= th + GAP + MARGIN || spaceAbove >= spaceBelow ? 'top' : 'bottom';
      const top = placement === 'top'
        ? Math.max(MARGIN, r.top - th - GAP)
        : Math.min(vh - th - MARGIN, r.bottom + GAP);

      // Horizontal: centre on the trigger, then clamp within the viewport.
      const center = r.left + r.width / 2;
      const left = Math.max(MARGIN, Math.min(center - tw / 2, vw - tw - MARGIN));
      const arrowLeft = Math.max(10, Math.min(center - left, tw - 10));

      setTip({ top, left, placement, arrowLeft, ready: true });
    };

    compute();
    // Second pass after layout settles (fonts/wrapping) to refine measurements.
    const raf = requestAnimationFrame(compute);
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [isPopupOpen, comment]);

  // Handle mouse enter with 200ms delay for popup
  const handleMouseEnter = useCallback(() => {
    if (!comment) return;
    hoverTimeoutRef.current = setTimeout(() => {
      onOpenPopup();
    }, 200);
  }, [comment, onOpenPopup]);

  // Handle mouse leave - clear timeout and close popup
  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    onClosePopup();
  }, [onClosePopup]);

  // Handle tap/click for touch devices (toggle behavior)
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!comment) return;
    e.preventDefault();
    e.stopPropagation();
    if (isPopupOpen) {
      onClosePopup();
    } else {
      onOpenPopup();
    }
  }, [comment, isPopupOpen, onOpenPopup, onClosePopup]);


  // If no comment, just render the formatted bid
  if (!comment) {
    return <>{formatBid(bid)}</>;
  }

  // Render bid with comment popup
  return (
    // inline-flex + min 24×24 keeps the tappable hit-area at the FR-021a
    // touch-target floor even though the bid glyph itself is only ~15 px tall in
    // the dense auction grid. Before feature 014 only commented bids became
    // role="button" cells; now per-bid BBO alerts make far more bids interactive
    // (e.g. the 1♣/1♠ openers on counting-practice), so the hit-area must satisfy
    // the floor. The glyph stays centered and unchanged in size — only the
    // clickable box grows, so the grid layout is untouched.
    <div className="relative inline-block">
      <span
        ref={triggerRef}
        className="inline-flex items-center justify-center min-w-[24px] min-h-[24px] bg-yellow-200 px-1 rounded cursor-pointer hover:bg-yellow-300 transition-colors align-middle"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        aria-label={`${bid} - ${comment.replace(/\n/g, '; ')}`}
        aria-expanded={isPopupOpen}
      >
        {formatBid(bid)}
      </span>

      {/* Popup tooltip — portalled to document.body so it escapes the auction
          card's `overflow-hidden` clipping, with a viewport-aware fixed position
          computed above (flips above/below, clamps horizontally). Font: 14px on
          small phones (≤ ~375px), 16px from the iPhone-14 class (≥ 390px) up. */}
      {isPopupOpen && createPortal(
        <div
          ref={tooltipRef}
          className={`fixed z-[100] px-2 py-1 bg-felt-deep text-white text-sm phone-lg:text-base rounded shadow-lg whitespace-pre-line break-words max-w-[16rem] w-max pointer-events-none transition-opacity ${
            tip.ready ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ top: tip.top, left: tip.left }}
          role="tooltip"
        >
          {comment}
          {/* Arrow points back at the trigger; sits on the side facing it. */}
          <div
            className={`absolute border-4 border-transparent ${
              tip.placement === 'top' ? 'top-full border-t-gray-800' : 'bottom-full border-b-gray-800'
            }`}
            style={{ left: tip.arrowLeft, transform: 'translateX(-50%)' }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
